import { useMemo } from "react";

/**
 * Hook para calcular el progreso de subtareas de cada tarea
 * Calcula qué porcentaje de subtareas están terminadas para cada tarea
 *
 * @param {Array} subtareas - Array de subtareas del cliente (de RTK Query)
 * @param {Array} tareas - Array de tareas del cliente (filtradas o no)
 * @returns {Object} Subtareas y porcentajes calculados
 * @returns {Array} return.subtareas - Array de subtareas (mismo que input)
 * @returns {Array} return.porcentajes - Array de objetos {idTarea, porcentaje}
 *
 * @example
 * // Uso básico en TablaInicioCliente
 * const { data: subTareasData = [] } = useObtenerSubtareasClienteQuery(rut);
 * const { subtareas, porcentajes } = useSubtareasProgreso(subTareasData, tareasCliente);
 *
 * // Luego en el render:
 * const porcentaje = porcentajes.find(p => p.idTarea === tarea.id)?.porcentaje || 0;
 *
 * @example
 * // Con validación
 * const { subtareas, porcentajes } = useSubtareasProgreso(subTareasData, tareasCliente);
 * if (porcentajes.length > 0) {
 *   // usar porcentajes...
 * }
 */
export const useSubtareasProgreso = (subtareas = [], tareas = []) => {
  const porcentajes = useMemo(() => {
    if (!Array.isArray(subtareas) || !Array.isArray(tareas)) {
      return [];
    }

    if (subtareas.length === 0 || tareas.length === 0) {
      return [];
    }

    // Calcular porcentaje para cada tarea
    return tareas.map((tarea) => {
      // Filtrar subtareas de esta tarea específica
      const subtareasTarea = subtareas.filter((s) => s.idTarea === tarea.id);

      // Filtrar subtareas terminadas
      const subtareasTerminadas = subtareasTarea.filter(
        (s) => s.estadoSubTarea === "TERMINADA",
      );

      // Calcular porcentaje (0 si no hay subtareas)
      const porcentaje =
        subtareasTarea.length === 0
          ? 0
          : (subtareasTerminadas.length / subtareasTarea.length) * 100;

      return { idTarea: tarea.id, porcentaje };
    });
  }, [subtareas, tareas]);

  return {
    subtareas,
    porcentajes,
  };
};
